import { useState } from "react";
import useAssignOrderToCourier from "api/OrderResource/useAssignOrderToCourier";
import { ConfirmPopup } from "primereact/confirmpopup";
import { CgArrowsExchangeAlt } from "react-icons/cg";
import { InputText } from "primereact/inputtext";
import { FiSearch } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { getHost } from "utils";

/**
 * @param {Object} props
 * @param {string} props.orderId
 * @param {Array<{id: string, first_name: string, last_name: string, email: string}>} props.couriers
 * @returns {JSX.Element}
 */
const AssignOrderToCourier = (props) => {
	const { orderId, orderNumber, couriers } = props;
	const [visible, setVisible] = useState(false);
	const [selectedCourier, setSelectedCourier] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");

	const elementId = `assign-order-to-courier-${orderId}`;

	const handleCourierSelect = (courierId) => {
		setSelectedCourier(courierId);
	};

	const onHide = () => {
		setVisible(false);
		setSelectedCourier(null); // Reset selection when dialog is closed
		setSearchTerm(""); // Reset search when dialog is closed
	};

	const filteredCouriers = couriers?.filter((courier) => {
		const searchLower = searchTerm.toLowerCase();
		return (
			courier?.first_name?.toLowerCase().includes(searchLower) ||
			courier?.last_name?.toLowerCase().includes(searchLower) ||
			courier?.email?.toLowerCase().includes(searchLower)
		);
	});

	const orderUrl =
		getHost() + "/my-account/assigned-orders?search=" + orderNumber;

	const { mutateAsync: assignOrderToCourier, isLoading } =
		useAssignOrderToCourier();

	const handleAssign = async () => {
		try {
			await assignOrderToCourier({
				order_id: orderId,
				courier_id: selectedCourier,
				order_url: orderUrl,
			});
			setVisible(false);
		} catch (error) {
			setVisible(false);
		}
	};

	return (
		<>
			<button
				type="button"
				onClick={() => setVisible(true)}
				className="text-akgreener flex items-center gap-1 border border-akgreen px-2 py-1 rounded-full max-w-max"
				id={elementId}
			>
				Assign to courier
				{isLoading ? (
					<AiOutlineLoading3Quarters
						className="w-4 h-4 animate-spin"
						size={15}
					/>
				) : (
					<span className="w-4 h-4 border border-akgreener rounded-full" />
				)}
			</button>

			<ConfirmPopup
				target={document.getElementById(elementId)}
				visible={visible}
				onHide={onHide}
				className="font-sans text-sm"
				message={
					<div className="space-y-2 w-full pr-2">
						<CgArrowsExchangeAlt
							size={50}
							className="text-akgreener"
						/>
						<div
							className="flex flex-col "
							style={{ width: 300 }}
						>
							<p className="text-base font-bold">Assign order to courier</p>
							<p>Select a courier to assign the order to</p>
						</div>

						<div className="relative">
							<span className="p-input-icon-left w-full">
								<FiSearch className="text-gray-500 " />
								<InputText
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									placeholder="Search couriers..."
									className="w-full p-2 text-sm font-sans"
								/>
							</span>
						</div>

						<div
							className=""
							style={{ maxHeight: 300, overflowY: "auto" }}
						>
							{filteredCouriers?.length === 0 ? (
								<div className="text-center text-gray-500 py-4">
									No couriers found
								</div>
							) : (
								filteredCouriers?.map((courier) => (
									<button
										type="button"
										key={courier?.id}
										onClick={() => handleCourierSelect(courier?.id)}
										className={`block w-full text-left px-2 py-2 rounded transition-colors ${
											selectedCourier === courier?.id
												? "bg-akgreener bg-opacity-10 border border-akgreener"
												: "hover:bg-gray-50"
										}`}
									>
										<div className="flex flex-col font-sans text-sm">
											<span className="font-bold">
												{courier?.first_name} {courier?.last_name}
											</span>
											<span className="text-sm text-gray-600">
												{courier?.email}
											</span>
										</div>
									</button>
								))
							)}
						</div>
					</div>
				}
				acceptLabel="Assign"
				rejectLabel="Cancel"
				acceptClassName={selectedCourier ? "" : "hidden"}
				accept={handleAssign}
			/>
		</>
	);
};

export default AssignOrderToCourier;
